import { Product } from "../../stores/appReportsStore";
import { Platform } from "../../types/Platforms";
import { getAccessToken } from "../authentication/supabaseService";
import { RawProductResponse } from "../import/importProductIdsService";
import { SourcingRequestItem } from "./sourcingOptionsService";

export interface SupplierProduct {
  name: string;
  articleId: string;
  price: number;
  unitPrice: number;
  unitPriceUnit: string;
  standardizedAmount: number;
  stdUnit: string;
  packLabel: string;
  category: string;
}

interface RawCompareResponse {
  sourcing_options: Record<string, RawProductResponse[]>;
  supplierName: string;
  extractedCount: number;
  supplierProducts: SupplierProduct[];
}

// Supplier products aren't a real platform; never let an unexpected value crash the mapping.
function safePlatform(value: string): Platform {
  try {
    return Platform.fromString(value);
  } catch {
    return Platform.fromString('any_invoice');
  }
}

export interface SupplierComparisonResult {
  optionsByArticleId: Record<string, Product[]>;
  supplierName: string;
  extractedCount: number;
  supplierProducts: SupplierProduct[];
}

/**
 * Compares the period's order-history products against an uploaded new-supplier
 * price file. Sends the file(s) + the input products as multipart; returns the
 * per-product supplier alternatives mapped to the shared Product shape (no parent
 * injection — options are purely the supplier's products).
 */
export async function compareSupplier(
  files: File[],
  items: SourcingRequestItem[],
  restaurantId: string,
  supplierName: string
): Promise<SupplierComparisonResult> {
  const token = await getAccessToken();

  const form = new FormData();
  for (const file of files) form.append('files', file);
  form.append('products', JSON.stringify(items));
  form.append('restaurantId', restaurantId);
  form.append('supplierName', supplierName);

  const res = await fetch('/api/compareSupplier', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to compare supplier');
  }
  const data = (await res.json()) as RawCompareResponse;

  const optionsByArticleId: Record<string, Product[]> = {};
  for (const [id, list] of Object.entries(data.sourcing_options)) {
    optionsByArticleId[id] = list.map((raw) => {
      const single = raw.quantityInfo.single;
      const comboInfo = raw.quantityInfo.combo;
      return {
        platform: safePlatform(raw.platform),
        category: raw.category,
        name: raw.name,
        brand: raw.brand,
        articleId: raw.articleId,
        src: raw.src,
        link: raw.link,
        matchScore: raw.matchScore,
        price: raw.price,
        isKiloPrice: raw.isKiloPrice,
        unitPrice: raw.unitPrice,
        unitPriceUnit: raw.unitPriceUnit,
        quantity: raw.quantity,
        restaurantName: raw.restaurantName,
        useSingle: true,
        useCombo: false,
        standardUnit: single.standardUnit,
        standardizedAmount: single.standardizedAmount,
        estimatedWeightPerPiece: single.estimatedWeightPerPiece,
        hasComboOption: raw.hasComboOption,
        combo: raw.hasComboOption && comboInfo
          ? {
              price: raw.comboPrice!,
              multiplier: raw.comboMultiplier!,
              standardizedAmount: comboInfo.standardizedAmount,
            }
          : undefined,
      };
    });
  }

  return {
    optionsByArticleId,
    supplierName: data.supplierName,
    extractedCount: data.extractedCount,
    supplierProducts: data.supplierProducts ?? [],
  };
}
